import StaticPage from "@/components/StaticPage";
import { Lock, Shield, Server, Mail, KeyRound, Eye } from "lucide-react";

const badges = [
  { icon: Lock, title: "Encryption in transit", body: "All traffic between your browser, our API, and every subprocessor uses TLS 1.2+ (HTTPS). No plaintext connections." },
  { icon: Server, title: "Encryption at rest", body: "MongoDB Atlas stores your data on encrypted volumes (AES-256). Backups are encrypted with a separate key." },
  { icon: KeyRound, title: "Password hashing", body: "Passwords are hashed with bcrypt (12 rounds). We can't see them, and neither can our infra vendors — even if they wanted to." },
  { icon: Shield, title: "Rate limiting + brute-force protection", body: "Login and password-reset endpoints throttle repeat attempts per email and IP. Reset tokens are single-use and expire in an hour." },
  { icon: Eye, title: "Minimal access", body: "Only the founder has production access, protected by 2FA. Every access event is logged." },
  { icon: Mail, title: "Deliverability & compliance", body: "Every outbound message has an unsubscribe link; unsubscribes are honored immediately across every future send. We use Resend for delivery and follow CAN-SPAM, GDPR, and CASL basics." },
];

export default function Security() {
  return (
    <StaticPage eyebrow="Legal" title="Security" updatedAt="February 2026">
      <p className="text-lg text-[#183D36]">
        You&apos;re trusting NextVisit with your customer list, your voice, and your reputation. Here&apos;s how we protect that trust.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 my-8">
        {badges.map((b) => (
          <div key={b.title} className="rounded-2xl bg-white border border-[#E5E0D8] p-5">
            <span className="w-10 h-10 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center mb-3">
              <b.icon size={18} />
            </span>
            <h3 className="font-heading text-lg text-[#183D36] mb-1">{b.title}</h3>
            <p className="text-sm text-[#4A4A4A] leading-relaxed">{b.body}</p>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Infrastructure</h2>
      <p>
        NextVisit runs on managed cloud infrastructure. Data is stored in MongoDB Atlas (multi-region encrypted). Emails are delivered via Resend. Payments are processed by Stripe (PCI-DSS Level 1). We never store credit card numbers on our servers — Stripe handles that end-to-end.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Authentication</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li>Passwords are hashed with bcrypt (never stored in plaintext).</li>
        <li>Sessions use short-lived JWTs (24-hour expiry).</li>
        <li>Password reset links expire in 1 hour and are single-use.</li>
        <li>Team invite links expire in 7 days and are single-use.</li>
        <li>Failed login attempts are rate-limited per email and IP.</li>
      </ul>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Multi-tenant isolation</h2>
      <p>
        Every workspace&apos;s data is scoped by a <code>workspace_id</code> at the database query level. Cross-workspace access is impossible from any application code path — we have automated tests that verify this on every deploy.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Incident response</h2>
      <p>
        In the unlikely event of a security incident that affects your data, we&apos;ll notify affected account owners within 72 hours with details of what happened, what we did, and what you should do next.
      </p>

      <h2 className="font-heading text-2xl text-[#183D36] mt-10 mb-2">Reporting a vulnerability</h2>
      <p>
        Found something concerning? Please email <a className="text-[#183D36] underline" href="mailto:brooke@bookyournextvisit.com">brooke@bookyournextvisit.com</a> with details. We&apos;ll respond within 48 hours and credit you (with permission) once we&apos;ve fixed the issue.
      </p>
    </StaticPage>
  );
}
